import React from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import AppRouter from './AppRouter';
import AppProviders from './providers/AppProviders';
import { useAppStartup } from './startup/useAppStartup';
import Loading from '../shared/ui/Loading';
import { Login } from './Login';
import { AppStore, AppDispatch } from '../redux/store';

interface AppProps {
    msalInstance: IPublicClientApplication;
    store: AppStore;
    isInTeams: boolean;
}

interface AppInnerProps {
    msalInstance: IPublicClientApplication;
    dispatch: AppDispatch;
    isInTeams: boolean;
}

// AppInner must render inside AppProviders (MsalProvider) so MSAL is fully initialized
// before useAppStartup runs. For teams, only show login if auth fails to let the user retry w/ acquireTokenPopup
const AppInner: React.FC<AppInnerProps> = ({ msalInstance, dispatch, isInTeams }) => {
    const { isStartingUp, activeAccount, teamsAuthFailed } = useAppStartup({ msalInstance, dispatch });

    // Teams: show Login.tsx when silent + broker popup both failed, or startup resolved with no account.
    // Login.tsx will call acquireTokenPopup (broker) for retry, then toast on second failure.
    if (isInTeams && (teamsAuthFailed || (!isStartingUp && !activeAccount))) {
        return <Login />;
    }

    return isStartingUp ? <Loading text="Loading Chats..." /> : <AppRouter />;
};

export const App: React.FC<AppProps> = ({ msalInstance, store, isInTeams }) => {
    return (
        <AppProviders msalInstance={msalInstance} store={store}>
            {isInTeams ? (
                // Teams: render AppInner unconditionally inside MsalProvider.
                // useAppStartup handles the NAA broker cache race via loginTeamsAsync.
                <AppInner msalInstance={msalInstance} dispatch={store.dispatch} isInTeams={true} />
            ) : (
                // Browser: gate AppInner inside AuthenticatedTemplate so useAppStartup only
                // runs after MSAL confirms the user is authenticated. Without this gate,
                // an unauthenticated first load would hit the "no active account → logoutRedirect"
                // path in useAppStartup on every render, causing an infinite redirect loop.
                <>
                    <UnauthenticatedTemplate>
                        <Login />
                    </UnauthenticatedTemplate>

                    <AuthenticatedTemplate>
                        <AppInner msalInstance={msalInstance} dispatch={store.dispatch} isInTeams={false} />
                    </AuthenticatedTemplate>
                </>
            )}
        </AppProviders>
    );
};

export default App;
